import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Tooltip from '@mui/material/Tooltip';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import { COMMON_TIMEZONES } from '../../utils/reportConfig';
import { reportSchedulesApi } from '../../api/reportSchedules';
import { buildingCorpsApi } from '../../api/buildingCorps';

const WEEK_DAYS = [
  { value: 0, label: 'Понедельник' },
  { value: 1, label: 'Вторник' },
  { value: 2, label: 'Среда' },
  { value: 3, label: 'Четверг' },
  { value: 4, label: 'Пятница' },
  { value: 5, label: 'Суббота' },
  { value: 6, label: 'Воскресенье' },
];

const WEEKDAY_SHORT = [
  { value: 0, label: 'Пн' },
  { value: 1, label: 'Вт' },
  { value: 2, label: 'Ср' },
  { value: 3, label: 'Чт' },
  { value: 4, label: 'Пт' },
  { value: 5, label: 'Сб' },
  { value: 6, label: 'Вс' },
];

const PERIOD_OPTIONS = [
  { value: 0, label: 'Текущий месяц' },
  { value: -1, label: 'Предыдущий месяц' },
];

const createEmptyForm = (complex) => ({
  residential_complex_id: complex?.id || '',
  building_id: '',
  corps_id: '',
  enabled: true,
  schedule_type: 'monthly',
  day_of_week: 0,
  day_of_month: 1,
  time_of_day: '09:00',
  timezone: complex?.report_timezone || 'Europe/Moscow',
  excluded_weekdays: [],
  period_shift_months: -1,
  recipients: [],
  subject: '',
  message: '',
});

const ScheduleCard = ({ item, onEdit, onDelete, onTest, testing }) => {
  const enabledLabel = item.enabled ? 'Активен' : 'Выключен';
  const scheduleLabel =
    item.schedule_type === 'weekly'
      ? `Еженедельно • ${WEEK_DAYS.find((d) => d.value === item.day_of_week)?.label || '—'}`
      : item.schedule_type === 'daily'
        ? 'Ежедневно'
        : `Ежемесячно • ${item.day_of_month || '—'} число`;
  const periodLabel = item.period_shift_months === 0 ? 'Текущий месяц' : 'Предыдущий месяц';
  const excludedLabel = (item.excluded_weekdays || []).length
    ? `Исключая: ${(item.excluded_weekdays || [])
      .map((d) => WEEKDAY_SHORT.find((w) => w.value === d)?.label || '')
      .filter(Boolean)
      .join(', ')}`
    : '';
  const nextRun = item.next_run_at ? new Date(item.next_run_at).toLocaleString() : '—';
  const lastRun = item.last_run_at ? new Date(item.last_run_at).toLocaleString() : '—';
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: item.enabled ? 'rgba(76, 175, 80, 0.04)' : 'transparent',
        borderColor: item.enabled ? 'rgba(76, 175, 80, 0.35)' : 'divider',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.subject || 'Регламентный отчёт'}
            </Typography>
            <Chip size="small" label={enabledLabel} color={item.enabled ? 'success' : 'default'} />
            <Chip size="small" label={periodLabel} variant="outlined" />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScheduleRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {scheduleLabel} • {item.time_of_day} • {item.timezone}
            </Typography>
          </Stack>
          {excludedLabel && (
            <Typography variant="caption" color="text.secondary">
              {excludedLabel}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <MailOutlineRoundedIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {(item.recipients || []).join(', ') || '—'}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Следующий запуск: {nextRun} • Последний: {lastRun}
          </Typography>
          {item.last_run_status?.endsWith('error') && (
            <Alert severity="error" sx={{ py: 0, mt: 0.5 }}>
              {item.last_error || 'Не удалось отправить отчёт'}
            </Alert>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Тестовая отправка">
            <span>
              <IconButton size="small" onClick={() => onTest(item)} disabled={testing}>
                <SendRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => onEdit(item)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(item)}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const splitRecipients = (value) =>
  String(value || '')
    .split(/[,\s;]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const ReportSchedulesDialog = ({ open, onClose, complex, buildings = [] }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(createEmptyForm(complex));
  const [corpsOptions, setCorpsOptions] = useState([]);
  const [loadingCorps, setLoadingCorps] = useState(false);
  const [testingId, setTestingId] = useState('');
  const [recipientsInput, setRecipientsInput] = useState('');
  const [recipientsError, setRecipientsError] = useState('');

  const commitRecipientTokens = useCallback((rawValue, current = []) => {
    const tokens = splitRecipients(rawValue);
    if (!tokens.length) {
      return { next: current, invalid: [] };
    }
    const next = [...(current || [])];
    const invalid = [];
    tokens.forEach((token) => {
      if (!isValidEmail(token)) {
        invalid.push(token);
        return;
      }
      if (!next.includes(token)) {
        next.push(token);
      }
    });
    return { next, invalid };
  }, []);

  const applyRecipientInput = useCallback(
    (rawValue) => {
      if (!/[,\s;]/.test(String(rawValue || ''))) {
        return false;
      }
      const { next, invalid } = commitRecipientTokens(rawValue, form.recipients);
      setForm((prev) => ({ ...prev, recipients: next }));
      setRecipientsInput('');
      setRecipientsError(
        invalid.length ? `Некорректный email: ${invalid.join(', ')}` : ''
      );
      return true;
    },
    [commitRecipientTokens, form.recipients]
  );

  const buildingOptions = useMemo(
    () => buildings.map((b) => ({ id: String(b.id), name: b.name || b.address || 'Дом' })),
    [buildings],
  );

  const loadSchedules = async () => {
    if (!complex?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await reportSchedulesApi.list(String(complex.id));
      setSchedules(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить расписания');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm(createEmptyForm(complex));
      setEditingId('');
      setTestMessage('');
      setRecipientsInput('');
      setRecipientsError('');
      loadSchedules();
    }
  }, [open, complex?.id]); // eslint-disable-line

  useEffect(() => {
    const buildingId = form.building_id;
    if (!buildingId) {
      setCorpsOptions([]);
      return;
    }
    let mounted = true;
    setLoadingCorps(true);
    buildingCorpsApi
      .getByBuilding(buildingId)
      .then((data) => {
        if (!mounted) return;
        setCorpsOptions(data || []);
      })
      .catch(() => {
        if (!mounted) return;
        setCorpsOptions([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingCorps(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.building_id]);

  const handleEdit = (item) => {
    setEditingId(String(item.id));
    setRecipientsInput('');
    setRecipientsError('');
    setForm({
      residential_complex_id: item.residential_complex_id,
      building_id: item.building_id || '',
      corps_id: item.corps_id || '',
      enabled: Boolean(item.enabled),
      schedule_type: item.schedule_type || 'monthly',
      day_of_week: item.day_of_week ?? 0,
      day_of_month: item.day_of_month ?? 1,
      time_of_day: item.time_of_day || '09:00',
      timezone: item.timezone || 'Europe/Moscow',
      excluded_weekdays: item.excluded_weekdays || [],
      period_shift_months: item.period_shift_months ?? -1,
      recipients: Array.isArray(item.recipients) ? item.recipients : [],
      subject: item.subject || '',
      message: item.message || '',
    });
  };

  const handleDelete = async (item) => {
    if (!item?.id) return;
    setSaving(true);
    try {
      await reportSchedulesApi.remove(String(item.id));
      await loadSchedules();
      if (editingId === String(item.id)) {
        setEditingId('');
        setForm(createEmptyForm(complex));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось удалить расписание');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    let recipients = [...(form.recipients || [])];
    if (recipientsInput.trim()) {
      const { next, invalid } = commitRecipientTokens(`${recipientsInput},`, recipients);
      if (invalid.length) {
        setRecipientsError(`Некорректный email: ${invalid.join(', ')}`);
        setError(`Некорректный email: ${invalid.join(', ')}`);
        return;
      }
      recipients = next;
      setForm((prev) => ({ ...prev, recipients: next }));
      setRecipientsInput('');
      setRecipientsError('');
    }
    if (!recipients.length) {
      setError('Укажите хотя бы один email.');
      return;
    }
    if (!form.time_of_day) {
      setError('Укажите время отправки.');
      return;
    }
    if (form.schedule_type === 'weekly' && form.day_of_week == null) {
      setError('Выберите день недели.');
      return;
    }
    if (form.schedule_type === 'monthly' && !form.day_of_month) {
      setError('Выберите день месяца.');
      return;
    }
    if (form.schedule_type === 'daily' && (form.excluded_weekdays || []).length === 7) {
      setError('Нельзя исключить все дни недели.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      building_id: form.building_id || null,
      corps_id: form.corps_id || null,
      recipients,
    };
    try {
      if (editingId) {
        await reportSchedulesApi.update(editingId, payload);
      } else {
        await reportSchedulesApi.create(payload);
      }
      setEditingId('');
      setForm(createEmptyForm(complex));
      await loadSchedules();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось сохранить расписание');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async (item) => {
    if (!item?.id) return;
    setTestingId(String(item.id));
    setError('');
    setTestMessage('');
    try {
      await reportSchedulesApi.test(String(item.id));
      setTestMessage('Тестовая рассылка отправлена.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailText = Array.isArray(detail)
        ? detail.map((item) => item?.msg || JSON.stringify(item)).join('; ')
        : detail;
      setError(detailText || err?.message || 'Не удалось отправить тест.');
    } finally {
      try {
        await loadSchedules();
      } catch (_) {
        // список обновим при следующем открытии
      }
      setTestingId('');
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Регламентные отчёты
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Автоматическая отправка отчётов по расписанию
          </Typography>
        </Stack>
        <IconButton
          aria-label="Закрыть"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {testMessage && <Alert severity="success">{testMessage}</Alert>}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ScheduleRoundedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Активные расписания
                </Typography>
              </Stack>
              {loading && <Typography color="text.secondary">Загрузка...</Typography>}
              {!loading && schedules.length === 0 && (
                <Typography color="text.secondary">
                  Пока нет настроенных расписаний. Создайте первое ниже.
                </Typography>
              )}
              {schedules.map((item) => (
                <ScheduleCard
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onTest={handleTestSend}
                  testing={testingId === String(item.id)}
                />
              ))}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddCircleOutlineRoundedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {editingId ? 'Редактирование расписания' : 'Новое расписание'}
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Тип расписания</InputLabel>
                  <Select
                    value={form.schedule_type}
                    label="Тип расписания"
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        schedule_type: event.target.value,
                        excluded_weekdays: event.target.value === 'daily' ? prev.excluded_weekdays : [],
                      }))
                    }
                  >
                    <MenuItem value="monthly">Ежемесячно</MenuItem>
                    <MenuItem value="daily">Ежедневно</MenuItem>
                    <MenuItem value="weekly">Еженедельно</MenuItem>
                  </Select>
                </FormControl>
                {form.schedule_type === 'weekly' ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>День недели</InputLabel>
                    <Select
                      value={form.day_of_week}
                      label="День недели"
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, day_of_week: Number(event.target.value) }))
                      }
                    >
                      {WEEK_DAYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : form.schedule_type === 'monthly' ? (
                  <TextField
                    label="День месяца"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1, max: 31 }}
                    value={form.day_of_month}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, day_of_month: Number(event.target.value) || 1 }))
                    }
                  />
                ) : null}
                {form.schedule_type === 'daily' && (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Исключить дни
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {WEEKDAY_SHORT.map((day) => {
                        const active = (form.excluded_weekdays || []).includes(day.value);
                        return (
                          <Chip
                            key={day.value}
                            label={day.label}
                            color={active ? 'primary' : 'default'}
                            variant={active ? 'filled' : 'outlined'}
                            size="small"
                            onClick={() => {
                              setForm((prev) => {
                                const current = new Set(prev.excluded_weekdays || []);
                                if (current.has(day.value)) {
                                  current.delete(day.value);
                                } else {
                                  current.add(day.value);
                                }
                                return { ...prev, excluded_weekdays: Array.from(current).sort() };
                              });
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                )}
                <TextField
                  label="Время отправки"
                  type="time"
                  size="small"
                  fullWidth
                  value={form.time_of_day}
                  onChange={(event) => setForm((prev) => ({ ...prev, time_of_day: event.target.value }))}
                />
                <Autocomplete
                  freeSolo
                  options={COMMON_TIMEZONES}
                  value={form.timezone || ''}
                  inputValue={form.timezone || ''}
                  onInputChange={(_, newValue) =>
                    setForm((prev) => ({ ...prev, timezone: newValue || '' }))
                  }
                  onChange={(_, newValue) =>
                    setForm((prev) => ({ ...prev, timezone: newValue || '' }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Часовой пояс" size="small" fullWidth />
                  )}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Период отчёта</InputLabel>
                  <Select
                    value={form.period_shift_months}
                    label="Период отчёта"
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, period_shift_months: Number(event.target.value) }))
                    }
                  >
                    {PERIOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Дом (опционально)</InputLabel>
                  <Select
                    value={form.building_id || ''}
                    label="Дом (опционально)"
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        building_id: event.target.value,
                        corps_id: '',
                      }))
                    }
                  >
                    <MenuItem value="">Все дома</MenuItem>
                    {buildingOptions.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" disabled={!form.building_id || loadingCorps}>
                  <InputLabel>Корпус (опционально)</InputLabel>
                  <Select
                    value={form.corps_id || ''}
                    label="Корпус (опционально)"
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, corps_id: event.target.value }))
                    }
                  >
                    <MenuItem value="">Все корпуса</MenuItem>
                    {corpsOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name || c.address || 'Корпус'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={form.recipients}
                  inputValue={recipientsInput}
                  onChange={(_, newValue) => {
                    const cleaned = (newValue || [])
                      .map((item) => String(item || '').trim())
                      .filter(Boolean);
                    const { next, invalid } = commitRecipientTokens(
                      cleaned.join(','),
                      []
                    );
                    setForm((prev) => ({ ...prev, recipients: next }));
                    setRecipientsError(
                      invalid.length ? `Некорректный email: ${invalid.join(', ')}` : ''
                    );
                  }}
                  onInputChange={(_, newInputValue) => {
                    if (!applyRecipientInput(newInputValue)) {
                      setRecipientsInput(newInputValue);
                    }
                  }}
                  filterOptions={(options) => options}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Получатели"
                      placeholder="email1@company.ru, email2@company.ru"
                      helperText={
                        recipientsError ||
                        'Enter, пробел или запятая добавляют адрес. Можно указать несколько.'
                      }
                      error={Boolean(recipientsError)}
                      size="small"
                      fullWidth
                      onBlur={() => {
                        if (!recipientsInput.trim()) return;
                        applyRecipientInput(`${recipientsInput},`);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ' || event.key === ',') {
                          event.preventDefault();
                          applyRecipientInput(`${recipientsInput},`);
                        }
                      }}
                    />
                  )}
                />
                <TextField
                  label="Тема письма"
                  size="small"
                  fullWidth
                  value={form.subject}
                  onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.enabled}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, enabled: event.target.checked }))
                      }
                    />
                  }
                  label="Включено"
                />
                <TextField
                  label="Сообщение"
                  multiline
                  minRows={3}
                  fullWidth
                  value={form.message}
                  onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                />
              </Stack>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <ApartmentRoundedIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Если дом не выбран, на почту придёт ZIP с отдельным отчётом по каждому дому.
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Закрыть
        </Button>
        {editingId && (
          <Button
            variant="outlined"
            onClick={() => {
              setEditingId('');
              setForm(createEmptyForm(complex));
              setRecipientsInput('');
              setRecipientsError('');
            }}
            disabled={saving}
          >
            Сбросить
          </Button>
        )}
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportSchedulesDialog;
